import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin, ArrowDown } from "lucide-react";
import { fileUrl } from "@/lib/api";
import { useLang } from "@/lib/i18n";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero({ settings }) {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const portraitY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const bgY = useTransform(scrollYProgress, [0, 1], [0, 160]);

  const go = (href) => document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0 pointer-events-none">
        <img
          src="https://images.unsplash.com/photo-1638376776402-9a4b75fe21bb?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt=""
          className="absolute right-0 top-0 w-[70%] h-full object-cover opacity-[0.08]"
        />
        <div className="absolute -top-40 -right-40 w-[700px] h-[700px] rounded-full bg-gold/[0.05] blur-[140px]" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] rounded-full bg-forest/20 blur-[160px]" />
      </motion.div>

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 pt-32 pb-20 grid lg:grid-cols-12 gap-16 items-center w-full">
        <div className="lg:col-span-7">
          <motion.p
            data-testid="hero-eyebrow"
            className="eyebrow mb-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.15 }}
          >
            {t("hero.eyebrow")}
          </motion.p>

          <h1 className="font-serif font-medium text-5xl sm:text-6xl lg:text-[5.2rem] leading-[1.02] tracking-tight text-ivory" data-testid="hero-headline">
            <MaskedLine delay={0.25}>{t("hero.h1a")}</MaskedLine>
            <MaskedLine delay={0.37}>{t("hero.h1b")}</MaskedLine>
            <MaskedLine delay={0.49}>
              <span className="italic text-gold-light">{t("hero.h1c")}</span>
            </MaskedLine>
          </h1>

          <motion.p
            data-testid="hero-subcopy"
            className="mt-8 max-w-xl text-base md:text-lg text-white/60 leading-relaxed"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.85 }}
          >
            {t("hero.sub")}
          </motion.p>

          <motion.div
            className="mt-10 flex flex-wrap items-center gap-4"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.0 }}
          >
            <button
              data-testid="hero-explore-btn"
              onClick={() => go("#bsgotech")}
              className="group px-8 py-4 bg-gold text-ink font-mono text-[11px] tracking-[0.2em] uppercase hover:bg-gold-light transition-colors duration-300"
            >
              {t("hero.cta1")}
            </button>
            <button
              data-testid="hero-connect-btn"
              onClick={() => go("#connect")}
              className="px-8 py-4 border border-white/20 text-ivory font-mono text-[11px] tracking-[0.2em] uppercase hover:border-gold hover:text-gold transition-colors duration-300"
            >
              {t("hero.cta2")}
            </button>
          </motion.div>

          <motion.div
            data-testid="hero-location"
            className="mt-12 flex items-center gap-3 text-white/40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.2 }}
          >
            <MapPin size={14} className="text-gold" />
            <span className="font-mono text-[11px] tracking-[0.2em] uppercase">
              {t("hero.location")}
            </span>
          </motion.div>
        </div>

        <motion.div
          className="lg:col-span-5 relative"
          style={{ y: portraitY }}
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="relative mx-auto max-w-[420px]">
            <div className="absolute -top-4 -left-4 w-16 h-16 border-t border-l border-gold/70" />
            <div className="absolute -bottom-4 -right-4 w-16 h-16 border-b border-r border-gold/70" />
            <div
              data-testid="hero-portrait-frame"
              className="relative aspect-[3/4] bg-surface border border-white/10 overflow-hidden"
            >
              {settings?.portrait_url ? (
                <img src={fileUrl(settings.portrait_url)} alt="Cleiton Barreto dos Santos" className="w-full h-full object-cover" />
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <div className="w-28 h-28 rounded-full border border-gold/40 flex items-center justify-center">
                    <span className="font-serif text-5xl text-gold/80">C</span>
                  </div>
                  <p className="mt-8 font-mono text-[10px] tracking-[0.35em] uppercase text-white/30">{t("hero.portrait")}</p>
                </div>
              )}
              <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-ink/90 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <p className="font-serif text-2xl text-ivory">Cleiton Barreto dos Santos</p>
                <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-gold mt-1">{t("hero.role")}</p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      <motion.button
        data-testid="hero-scroll-indicator"
        onClick={() => go("#about")}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/30 hover:text-gold transition-colors"
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
        aria-label="Scroll down"
      >
        <ArrowDown size={18} />
      </motion.button>
    </section>
  );
}
